import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Spinner, Alert, Button, Badge, Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUsers, faInfoCircle, faCircle } from '@fortawesome/free-solid-svg-icons';
import MessageList from './MessageList';
import MessageComposer from './MessageComposer';
import ThreadHeader from './ThreadHeader';
import ReplyModal from './ReplyModal';
import { getThreadMessages, sendMessage, deleteMessage } from '../../services/messagingService';
import { useWebSocketMessages, useTypingIndicator, useTypingIndicatorSender } from '../../hooks/useWebSocket';
import './MessageThread.css';

function MessageThread({ thread, currentUserId }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sending, setSending] = useState(false);
  const [typingUsers, setTypingUsers] = useState(new Set());
  const [showReplyModal, setShowReplyModal] = useState(false);
  const [replyMessage, setReplyMessage] = useState(null);
  const [replyType, setReplyType] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const messagesEndRef = useRef(null);
  const messagesTopRef = useRef(null);
  const typingTimeoutsRef = useRef({});

  // Hook for sending typing indicators
  const sendTyping = useTypingIndicatorSender(thread?.threadId);

  // Handle incoming real-time messages
  const handleNewMessage = useCallback((message) => {
    // Only add message if it belongs to current thread
    if (message.threadId === thread?.threadId) {
      setMessages(prev => {
        // Check if message already exists (avoid duplicates)
        const exists = prev.some(m => m.messageId === message.messageId);
        if (exists) {
          return prev;
        }
        return [...prev, message];
      });
      scrollToBottom();
    }
  }, [thread?.threadId]);

  // Handle typing indicators
  const handleTypingIndicator = useCallback((data) => {
    if (data.threadId !== thread?.threadId) {
      return;
    }

    const { userId, isTyping } = data;

    // Clear existing timeout for this user
    if (typingTimeoutsRef.current[userId]) {
      clearTimeout(typingTimeoutsRef.current[userId]);
      delete typingTimeoutsRef.current[userId];
    }

    if (isTyping) {
      // Add user to typing set
      setTypingUsers(prev => new Set([...prev, userId]));

      // Auto-remove after 3 seconds
      typingTimeoutsRef.current[userId] = setTimeout(() => {
        setTypingUsers(prev => {
          const updated = new Set(prev);
          updated.delete(userId);
          return updated;
        });
        delete typingTimeoutsRef.current[userId];
      }, 3000);
    } else {
      // Remove user from typing set
      setTypingUsers(prev => {
        const updated = new Set(prev);
        updated.delete(userId);
        return updated;
      });
    }
  }, [thread?.threadId]);

  // Subscribe to WebSocket messages
  useWebSocketMessages(handleNewMessage);
  useTypingIndicator(handleTypingIndicator);

  useEffect(() => {
    if (thread?.threadId) {
      loadMessages();
      // Clear typing users when switching threads
      setTypingUsers(new Set());
      // Reset pagination
      setPage(1);
      setHasMore(false);
    }
  }, [thread?.threadId]);

  // Cleanup typing timeouts on unmount
  useEffect(() => {
    return () => {
      Object.values(typingTimeoutsRef.current).forEach(timeout => {
        clearTimeout(timeout);
      });
    };
  }, []);

  const loadMessages = async (pageNum = 1, prepend = false) => {
    try {
      if (prepend) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      setError(null);
      
      const response = await getThreadMessages(thread.threadId, { page: pageNum, limit: 50 });
      
      // Handle both array response and paginated response
      const data = Array.isArray(response) ? response : response.messages || [];
      const pagination = response.pagination || {};
      
      if (prepend) {
        // Prepend older messages to the beginning
        setMessages(prev => [...data, ...prev]);
      } else {
        setMessages(data);
        scrollToBottom();
      }
      
      setHasMore(pagination.hasNext || false);
      setPage(pageNum);
    } catch (err) {
      setError('Failed to load messages');
      console.error('Error loading messages:', err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadOlderMessages = () => {
    if (!loadingMore && hasMore) {
      loadMessages(page + 1, true);
    }
  };

  const handleSendMessage = async (content) => {
    if (!content.trim()) return;

    try {
      setSending(true);
      const newMessage = await sendMessage({
        threadId: thread.threadId,
        content: content.trim(),
        recipients: thread.participantDetails.map(p => p.userId)
      });

      // Message will be added via WebSocket callback
      // But add optimistically for immediate feedback
      setMessages(prev => [...prev, newMessage]);
      scrollToBottom();
    } catch (err) {
      console.error('Error sending message:', err);
      throw err;
    } finally {
      setSending(false);
    }
  };

  const handleTyping = () => {
    sendTyping();
  };

  const handleReply = (message, type) => {
    setReplyMessage(message);
    setReplyType(type);
    setShowReplyModal(true);
  };

  const handleSendReply = async (replyData) => {
    try {
      setSending(true);
      const newMessage = await sendMessage(replyData);
      
      // Message will be added via WebSocket callback
      // But add optimistically for immediate feedback
      setMessages(prev => [...prev, newMessage]);
      scrollToBottom();
      setShowReplyModal(false);
    } catch (err) {
      console.error('Error sending reply:', err);
      throw err;
    } finally {
      setSending(false);
    }
  };

  const handleDeleteRequest = (message) => {
    setDeleteConfirm(message);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm) return;

    try {
      await deleteMessage(deleteConfirm.messageId);
      
      // Remove message from local state
      setMessages(prev => prev.filter(m => m.messageId !== deleteConfirm.messageId));
      setDeleteConfirm(null);
    } catch (err) {
      console.error('Error deleting message:', err);
      setError('Failed to delete message');
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirm(null);
  };

  // Get typing user names
  const getTypingUserNames = () => {
    if (typingUsers.size === 0) {
      return null;
    }

    const names = Array.from(typingUsers)
      .map(userId => {
        const participant = thread.participantDetails.find(p => p.userId === userId);
        return participant?.displayName || 'Someone';
      })
      .filter(name => name !== currentUserId);

    if (names.length === 0) {
      return null;
    }

    if (names.length === 1) {
      return `${names[0]} is typing...`;
    } else if (names.length === 2) {
      return `${names[0]} and ${names[1]} are typing...`;
    } else {
      return `${names[0]} and ${names.length - 1} others are typing...`;
    }
  };

  const scrollToBottom = () => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  if (!thread) {
    return null;
  }

  const typingText = getTypingUserNames();

  return (
    <div className="message-thread">
      <ThreadHeader thread={thread} />

      <div className="message-thread-body">
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2 text-muted">Loading messages...</p>
          </div>
        ) : error ? (
          <Alert variant="danger" className="m-3">
            <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
            {error}
            <Button variant="link" onClick={loadMessages} className="ms-2">
              Try again
            </Button>
          </Alert>
        ) : (
          <>
            {/* Load older messages button */}
            {hasMore && (
              <div className="text-center py-2" ref={messagesTopRef}>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={loadOlderMessages}
                  disabled={loadingMore}
                >
                  {loadingMore ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Loading older messages...
                    </>
                  ) : (
                    'Load Older Messages'
                  )}
                </Button>
              </div>
            )}
            
            {thread.threadType === 'group' && (
              <div className="group-info-banner">
                <FontAwesomeIcon icon={faUsers} className="me-2" />
                Group conversation with {thread.participantDetails.length} participants
              </div>
            )}
            <MessageList 
              messages={messages} 
              currentUserId={currentUserId || "current-user-id"}
              onReply={handleReply}
              onDelete={handleDeleteRequest}
            />
            <div ref={messagesEndRef} />
            
            {/* Typing indicator */}
            {typingText && (
              <div className="typing-indicator">
                <FontAwesomeIcon icon={faCircle} className="typing-dot" />
                <FontAwesomeIcon icon={faCircle} className="typing-dot" />
                <FontAwesomeIcon icon={faCircle} className="typing-dot" />
                <span className="typing-text ms-2">{typingText}</span>
              </div>
            )}
          </>
        )}
      </div>

      <MessageComposer
        onSend={handleSendMessage}
        onTyping={handleTyping}
        disabled={sending || loading}
        placeholder={
          thread.threadType === 'group'
            ? 'Message to group...'
            : `Message ${thread.participantDetails[0]?.displayName || 'user'}...`
        }
      />

      {/* Reply Modal */}
      <ReplyModal
        show={showReplyModal}
        onHide={() => setShowReplyModal(false)}
        onSend={handleSendReply}
        originalMessage={replyMessage}
        thread={thread}
        replyType={replyType}
      />

      {/* Delete Confirmation Modal */}
      <Modal show={!!deleteConfirm} onHide={handleDeleteCancel}>
        <Modal.Header closeButton>
          <Modal.Title>Delete Message</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete this message?</p>
          <p className="text-muted small">
            This will only delete the message from your view. Other participants will still see it.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleDeleteCancel}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteConfirm}>
            Delete for Me
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default MessageThread;
